import { useEffect, useMemo, useState } from "react";
import { formatRelative } from "../OrgOverview/format";
import {
  formatDuration,
  formatEvalMoney,
  formatMoneyPairText,
  getDifferences,
  getGoldenLoanId,
  getHumanAmount,
  getJudgeVerdict,
  getLoanName,
  getSourceAmount,
  getSourceAvailableAmount,
  getSuiteRunId,
  normalizeStatus,
} from "./goldenEval";
import GoldenLoanEvalDetail from "./GoldenLoanEvalDetail";

function StatusPill({ status }) {
  const normalized = normalizeStatus(status);

  return (
    <span className={`icr-pill icr-pill--${normalized}`}>
      {normalized.replace(/-/g, " ")}
    </span>
  );
}

export default function GoldenSuiteDetail({
  env,
  run,
  loans,
  loading,
  recentRuns,
  onRefresh,
}) {
  const [selectedLoanId, setSelectedLoanId] = useState("");
  const selectedLoan = useMemo(
    () =>
      loans.find((loan) => getGoldenLoanId(loan) === selectedLoanId) ||
      loans[0] ||
      null,
    [loans, selectedLoanId],
  );

  useEffect(() => {
    if (
      selectedLoanId &&
      loans.some((loan) => getGoldenLoanId(loan) === selectedLoanId)
    ) {
      return;
    }
    setSelectedLoanId(getGoldenLoanId(loans[0]));
  }, [loans, selectedLoanId]);

  if (!run) {
    return null;
  }

  return (
    <section className="icr-eval-section" aria-labelledby="suite-detail-title">
      <div className="icr-eval-section__header">
        <div>
          <span className="icr-eval-eyebrow">Selected suite</span>
          <h2 id="suite-detail-title">Suite detail</h2>
          <p>
            {formatRelative(run.createdAt || run.startedAt)} ·{" "}
            <span className="icr-mono">{getSuiteRunId(run)}</span>
          </p>
        </div>
        <div className="icr-golden-actions">
          <StatusPill status={run.status} />
          <button className="icr-btn" type="button" onClick={onRefresh}>
            {loading ? "Loading…" : "Refresh detail"}
          </button>
        </div>
      </div>

      <div className="icr-table-scroll">
        <table className="icr-suite-detail-table">
          <thead>
            <tr>
              <th>Golden loan</th>
              <th className="icr-num">Human (optional)</th>
              <th className="icr-num">Addy</th>
              <th className="icr-num">Harness</th>
              <th>Judge verdict</th>
              <th>Differences</th>
              <th>Duration</th>
              <th>Concise explanation</th>
              <th>Drilldown</th>
            </tr>
          </thead>
          <tbody>
            {loans.map((loan) => {
              const id = getGoldenLoanId(loan);
              const differences = getDifferences(loan);
              const explanation =
                loan.explanation ||
                loan.judge?.explanation ||
                loan.summary ||
                Object.values(loan.explanations || {})
                  .map((record) =>
                    typeof record === "string" ? record : record?.text,
                  )
                  .find(Boolean) ||
                "No explanation provided.";

              return (
                <tr
                  key={id}
                  className={id === getGoldenLoanId(selectedLoan) ? "is-selected" : ""}
                >
                  <td>
                    <strong>{getLoanName(loan)}</strong>
                    <small className="icr-mono">{id}</small>
                  </td>
                  <td className="icr-num">{formatEvalMoney(getHumanAmount(loan))}</td>
                  <td className="icr-num icr-money-pair-cell">
                    <span title={formatMoneyPairText(
                      getSourceAvailableAmount(loan.addy),
                      getSourceAmount(loan.addy),
                    )}>
                      {formatMoneyPairText(
                        getSourceAvailableAmount(loan.addy),
                        getSourceAmount(loan.addy),
                      )}
                    </span>
                  </td>
                  <td className="icr-num icr-money-pair-cell">
                    <span title={formatMoneyPairText(
                      getSourceAvailableAmount(loan.harness),
                      getSourceAmount(loan.harness),
                    )}>
                      {formatMoneyPairText(
                        getSourceAvailableAmount(loan.harness),
                        getSourceAmount(loan.harness),
                      )}
                    </span>
                  </td>
                  <td>
                    <span className="icr-judge-verdict">
                      {getJudgeVerdict(loan)}
                    </span>
                    <small>Harness reviewer</small>
                  </td>
                  <td>
                    {differences.length}{" "}
                    {differences.length === 1 ? "difference" : "differences"}
                  </td>
                  <td>
                    {formatDuration(loan.durationMs || loan.elapsedMs)}
                  </td>
                  <td className="icr-suite-detail-table__explanation">
                    {explanation}
                  </td>
                  <td>
                    <button
                      className="icr-btn"
                      type="button"
                      aria-pressed={id === getGoldenLoanId(selectedLoan)}
                      onClick={() => setSelectedLoanId(id)}
                    >
                      {id === getGoldenLoanId(selectedLoan) ? "Selected" : "Inspect"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && loans.length === 0 && (
              <tr>
                <td colSpan={9} className="icr-muted">
                  This suite run has no loan results yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedLoan && (
        <GoldenLoanEvalDetail
          env={env}
          loan={selectedLoan}
          suiteRun={run}
          recentRuns={recentRuns}
        />
      )}
    </section>
  );
}
